import { ScrollArea } from '@radix-ui/react-scroll-area';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { ISuppliars } from '../../api/HandRequest.interface';

interface VirtualizedCommandProps {
  height: string;
  options: ISuppliars[];
  placeholder: string;
  selectedOption: ISuppliars | null;
  onSelectOption?: (option: ISuppliars) => void;
}

const VirtualizedCommand = ({
  height,
  options,
  placeholder,
  selectedOption,
  onSelectOption,
}: VirtualizedCommandProps) => {
  const [filteredOptions, setFilteredOptions] =
    React.useState<ISuppliars[]>(options);
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const [isKeyboardNavActive, setIsKeyboardNavActive] = React.useState(false);

  const parentRef = React.useRef(null);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  });

  const virtualOptions = virtualizer.getVirtualItems();

  const scrollToIndex = (index: number) => {
    virtualizer.scrollToIndex(index, {
      align: 'center',
    });
  };

  const handleSearch = (search: string) => {
    setIsKeyboardNavActive(false);
    setFilteredOptions(
      options.filter((option) =>
        option.ClientName.toLowerCase().includes(search.toLowerCase() ?? []),
      ),
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        setIsKeyboardNavActive(true);
        setFocusedIndex((prev) => {
          const newIndex =
            prev === -1 ? 0 : Math.min(prev + 1, filteredOptions.length - 1);
          scrollToIndex(newIndex);
          return newIndex;
        });
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        setIsKeyboardNavActive(true);
        setFocusedIndex((prev) => {
          const newIndex =
            prev === -1 ? filteredOptions.length - 1 : Math.max(prev - 1, 0);
          scrollToIndex(newIndex);
          return newIndex;
        });
        break;
      }
      case 'Enter': {
        event.preventDefault();
        if (filteredOptions[focusedIndex]) {
          onSelectOption?.(filteredOptions[focusedIndex]);
        }
        break;
      }
      default:
        break;
    }
  };

  React.useEffect(() => {
    if (selectedOption) {
      const option = filteredOptions.find(
        (option) => option.ID === selectedOption?.ID,
      );
      if (option) {
        const index = filteredOptions.indexOf(option);
        setFocusedIndex(index);
        virtualizer.scrollToIndex(index, {
          align: 'center',
        });
      }
    }
  }, [selectedOption, filteredOptions, virtualizer]);

  return (
    <Command shouldFilter={false} onKeyDown={handleKeyDown}>
      <CommandInput onValueChange={handleSearch} placeholder={placeholder} />
      <CommandList
        ref={parentRef}
        style={{
          height: height,
          width: '100%',
          overflow: 'auto',
        }}
        onMouseDown={() => setIsKeyboardNavActive(false)}
        onMouseMove={() => setIsKeyboardNavActive(false)}
      >
        <CommandEmpty>لا توجد نتائج للبحث</CommandEmpty>
        <ScrollArea>
          <CommandGroup>
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualOptions.map((virtualOption) => (
                <CommandItem
                  key={filteredOptions[virtualOption.index].ID}
                  disabled={isKeyboardNavActive}
                  className={cn(
                    'absolute left-0 top-0 w-full bg-transparent',
                    focusedIndex === virtualOption.index &&
                      'bg-accent text-accent-foreground',
                    isKeyboardNavActive &&
                      focusedIndex !== virtualOption.index &&
                      'aria-selected:bg-transparent aria-selected:text-primary',
                  )}
                  style={{
                    height: `${virtualOption.size}px`,
                    transform: `translateY(${virtualOption.start}px)`,
                  }}
                  value={`${filteredOptions[virtualOption.index].ID}`}
                  onMouseEnter={() =>
                    !isKeyboardNavActive && setFocusedIndex(virtualOption.index)
                  }
                  onMouseLeave={() =>
                    !isKeyboardNavActive && setFocusedIndex(-1)
                  }
                  onSelect={() =>
                    onSelectOption?.(filteredOptions[virtualOption.index])
                  }
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedOption?.ID ===
                        filteredOptions[virtualOption.index].ID
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  {filteredOptions[virtualOption.index].ClientName}
                </CommandItem>
              ))}
            </div>
          </CommandGroup>
        </ScrollArea>
      </CommandList>
    </Command>
  );
};

interface VirtualizedComboboxProps {
  options: ISuppliars[];
  searchPlaceholder?: string;
  onValueChange?: (value: ISuppliars | null) => void;
  width?: string;
  height?: string;
  value: ISuppliars | null;
}

export function AutoCompleteSuppliars({
  options,
  searchPlaceholder = 'Search items...',
  width = 'w-full',
  height = '400px',
  value,
  onValueChange,
}: VirtualizedComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const selectedName = value
    ? options.find((option) => option?.ID === value?.ID)?.ClientName
    : 'اختر مورد';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('group relative justify-between', width)}
        >
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex-1 truncate text-right">
                  {selectedName}
                </span>
              </TooltipTrigger>
              {value && (
                <TooltipContent side="top" align="start">
                  <p>{selectedName}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <div className="ml-2 flex flex-shrink-0 items-center gap-1">
            {value && (
              <X
                className="h-4 w-4 shrink-0 cursor-pointer text-destructive hover:text-destructive/90"
                onClick={(e) => {
                  e.stopPropagation();
                  onValueChange?.(null);
                }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <VirtualizedCommand
          height={height}
          options={options}
          placeholder={searchPlaceholder}
          selectedOption={value}
          onSelectOption={(currentValue) => {
            onValueChange?.(currentValue);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
